import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Sparkles, BookOpen, Feather, ArrowRight, Gift } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const [dailyPrompt, setDailyPrompt] = useState(null);
  const [wlEmail, setWlEmail] = useState('');
  const [wlStatus, setWlStatus] = useState('idle');

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!wlEmail.trim()) return;
    setWlStatus('sending');
    try {
      await axios.post(`${API}/public/waitlist`, { email: wlEmail.trim() });
      setWlStatus('done');
      setWlEmail('');
    } catch {
      setWlStatus('error');
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/public/prompt-of-the-day`)
      .then(({ data }) => setDailyPrompt(data))
      .catch(() => setDailyPrompt(null));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 overflow-hidden relative" data-testid="landing-page">
      {/* Soft ambient blobs */}
      <div className="absolute top-0 -left-32 w-96 h-96 bg-orange-200 rounded-full opacity-40 blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-0 -right-32 w-96 h-96 bg-rose-200 rounded-full opacity-40 blur-3xl" aria-hidden="true" />
      <div className="absolute top-1/2 left-1/3 w-72 h-72 bg-yellow-200 rounded-full opacity-30 blur-3xl" aria-hidden="true" />

      {/* Navigation */}
      <nav className="relative z-10 container mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="bg-white/60 backdrop-blur-sm p-2 rounded-full shadow-sm">
            <Feather className="h-5 w-5 text-amber-900" />
          </div>
          <span className="font-display text-xl font-bold text-amber-950" data-testid="nav-brand">Flash Fiction</span>
        </div>
        <div className="flex items-center space-x-1 sm:space-x-3">
          <Link to="/gallery">
            <Button variant="ghost" className="text-amber-900 hover:bg-amber-100 hidden sm:inline-flex" data-testid="nav-gallery-button">
              Gallery
            </Button>
          </Link>
          <Link to="/pricing">
            <Button variant="ghost" className="text-amber-900 hover:bg-amber-100 px-2 sm:px-4" data-testid="nav-pricing-button">
              Pricing
            </Button>
          </Link>
          <Link to="/about">
            <Button variant="ghost" className="text-amber-900 hover:bg-amber-100 hidden sm:inline-flex" data-testid="nav-about-button">
              About
            </Button>
          </Link>
          <Link to="/login">
            <Button variant="ghost" className="text-amber-900 hover:bg-amber-100 px-2 sm:px-4" data-testid="nav-login-button">
              Sign In
            </Button>
          </Link>
          <Link to="/register">
            <Button className="bg-amber-900 hover:bg-amber-800 text-amber-50 px-3 sm:px-4" data-testid="nav-signup-button">
              Get Started
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="relative z-10 container mx-auto px-6 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Text */}
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 bg-white/70 backdrop-blur-sm border border-amber-200 rounded-full px-4 py-2 shadow-sm">
              <Sparkles className="h-4 w-4 text-amber-700" />
              <span className="text-sm text-amber-900 font-medium">Powered by Claude Sonnet 4.5</span>
            </div>

            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl font-black text-amber-950 leading-none tracking-tight" data-testid="hero-headline">
              Flash
              <br />
              <span className="text-orange-800">Fiction</span>
            </h1>

            <Link to="/pricing" className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-300 text-orange-900 rounded-full px-4 py-2 text-sm font-semibold hover:bg-orange-500/20 transition-colors" data-testid="hero-trial-banner">
              <Gift className="h-4 w-4" />
              Free for 7 days &middot; 3 AI prompts a day &middot; no card needed
            </Link>

            <p className="font-serif-italic text-2xl md:text-3xl text-amber-800 leading-relaxed max-w-lg" data-testid="hero-tagline">
              &ldquo;The day rolled out its Autumn carpet beckoning us into her colorful gown&hellip;&rdquo;
            </p>

            <p className="text-lg text-amber-900/80 max-w-md leading-relaxed">
              Craft breath-taking short stories in minutes. AI-generated prompts across six genres, five moods, and one-word sparks &mdash; all yours to shape.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <Link to="/register">
                <Button
                  size="lg"
                  className="bg-amber-900 hover:bg-amber-800 text-amber-50 px-8 py-6 text-base rounded-full shadow-lg hover:shadow-xl transition-all group"
                  data-testid="hero-cta-primary"
                >
                  Start Writing
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-amber-900 text-amber-900 hover:bg-amber-100 px-8 py-6 text-base rounded-full"
                  data-testid="hero-cta-secondary"
                >
                  I have an account
                </Button>
              </Link>
            </div>
          </div>

          {/* Right: Hero collage image */}
          <div className="relative">
            <div className="relative aspect-square max-w-lg mx-auto" data-testid="tumbling-books-graphic">
              {/* Soft glow behind image */}
              <div className="absolute inset-0 bg-gradient-to-br from-amber-200/40 via-orange-200/40 to-rose-200/40 rounded-3xl blur-2xl transform scale-95" aria-hidden="true" />
              <img
                src="/images/hero.png"
                alt="Flash Fiction — an open book with autumn leaves scattering into the wind"
                className="relative w-full h-full object-contain rounded-2xl shadow-2xl ring-1 ring-amber-200/50"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>

        {/* Featured Prompt of the Day */}
        {dailyPrompt && (
          <div className="mt-20 lg:mt-28 max-w-3xl mx-auto" data-testid="prompt-of-the-day">
            <div className="text-center mb-6">
              <div className="inline-flex items-center space-x-2 text-amber-800 text-sm font-medium tracking-wider uppercase">
                <Sparkles className="h-4 w-4" />
                <span>Prompt of the Day</span>
              </div>
            </div>
            <div className="bg-white/60 backdrop-blur-sm border border-amber-200 rounded-2xl p-8 md:p-10 shadow-md">
              <p className="font-serif-italic text-2xl md:text-3xl text-amber-900 leading-relaxed text-center" data-testid="prompt-of-the-day-text">
                &ldquo;{dailyPrompt.prompt}&rdquo;
              </p>
              <div className="mt-6 text-center">
                <Link to="/register">
                  <Button variant="ghost" className="text-amber-800 hover:bg-amber-100 group" data-testid="prompt-of-the-day-cta">
                    Write from this prompt
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Feature strip */}
        <div className="mt-16 lg:mt-20 grid md:grid-cols-3 gap-6">
          <div className="bg-white/60 backdrop-blur-sm border border-amber-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="bg-amber-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
              <Sparkles className="h-6 w-6 text-amber-800" />
            </div>
            <h3 className="font-display text-xl font-bold text-amber-950 mb-2">AI Prompts</h3>
            <p className="text-amber-900/70 text-sm">Six categories × six styles including single-word sparks that ignite pages.</p>
          </div>
          <div className="bg-white/60 backdrop-blur-sm border border-amber-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
              <BookOpen className="h-6 w-6 text-orange-800" />
            </div>
            <h3 className="font-display text-xl font-bold text-amber-950 mb-2">Write Freely</h3>
            <p className="text-amber-900/70 text-sm">A distraction-free editor with live word counts made for 300&ndash;1000 word bursts.</p>
          </div>
          <div className="bg-white/60 backdrop-blur-sm border border-amber-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
            <div className="bg-rose-100 w-12 h-12 rounded-full flex items-center justify-center mb-4">
              <Feather className="h-6 w-6 text-rose-800" />
            </div>
            <h3 className="font-display text-xl font-bold text-amber-950 mb-2">Your Library</h3>
            <p className="text-amber-900/70 text-sm">Save, edit and revisit every story. Watch your body of work grow with milestones.</p>
          </div>
        </div>

        {/* Pricing teaser */}
        <div className="mt-16 lg:mt-20 max-w-4xl mx-auto" data-testid="landing-pricing-teaser">
          <div className="text-center mb-8">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-amber-950 mb-2">Try it free for a week. Then a few dollars a season.</h2>
            <p className="text-amber-900/70">Three AI prompts a day for 7 days, no card needed. Writers who stay pay once for a block of time — no auto‑renewals.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {[['$5', '3 months'], ['$15', '6 months'], ['$30', '12 months']].map(([price, label]) => (
              <Link key={label} to="/pricing" className="bg-white/60 backdrop-blur-sm border border-amber-100 rounded-2xl p-6 text-center hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={`teaser-plan-${label.split(' ')[0]}`}>
                <div className="font-display text-4xl font-black text-amber-950">{price}</div>
                <div className="text-sm text-amber-800/70 mt-1">{label}</div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/pricing">
              <Button variant="ghost" className="text-amber-800 hover:bg-amber-100 group" data-testid="teaser-pricing-cta">
                See what&rsquo;s included
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Waitlist */}
        <div className="mt-16 lg:mt-20 max-w-xl mx-auto text-center" data-testid="waitlist-section">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-amber-950 mb-2">Get early updates</h2>
          <p className="text-amber-900/70 mb-6">Leave your email and we&rsquo;ll let you know when new features land.</p>
          {wlStatus === 'done' ? (
            <p className="text-emerald-700 font-medium bg-emerald-50 border border-emerald-200 rounded-full px-6 py-3 inline-block" data-testid="waitlist-success">
              You&rsquo;re on the list! We&rsquo;ll be in touch.
            </p>
          ) : (
            <form onSubmit={handleWaitlist} className="flex flex-col sm:flex-row gap-3 justify-center" data-testid="waitlist-form">
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={wlEmail}
                onChange={(e) => setWlEmail(e.target.value)}
                className="flex-1 max-w-sm px-5 py-3 rounded-full border border-amber-300 bg-white/80 backdrop-blur-sm text-amber-950 placeholder:text-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="waitlist-email-input"
              />
              <Button
                type="submit"
                disabled={wlStatus === 'sending'}
                className="bg-amber-900 hover:bg-amber-800 text-amber-50 rounded-full px-8"
                data-testid="waitlist-submit-button"
              >
                {wlStatus === 'sending' ? 'Joining…' : 'Notify me'}
              </Button>
            </form>
          )}
          {wlStatus === 'error' && (
            <p className="text-rose-600 text-sm mt-3" data-testid="waitlist-error">Something went wrong — please try again.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Landing;
